import React, { FC, ReactNode, useMemo } from 'react';
import classNames from 'classnames';
import { MdOutlineLock } from 'react-icons/md';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { EventProductType } from 'api/dtos/events/products';
import useIdentifyLaunchDarkly from 'hooks/useIdentifyLaunchDarkly';
import { useEventEditorContext } from 'legacy/events/contexts/EventEditorContext';

type ProductTypeSelectorProps = {
  disabled?: boolean;
  isPrivate?: boolean;
};

type TypeButtonProps = {
  disabled: boolean;
  children: ReactNode;
  onClick: (id: EventProductType) => void;
  id: EventProductType;
  selected: boolean;
};

const TypeButton: FC<TypeButtonProps> = ({
  disabled,
  children,
  onClick,
  id,
  selected
}) => {
  const onButtonClick = () => onClick(id);

  return (
    <button
      className={classNames(
        'w-full text-center text-base font-semibold rounded-md py-2 cursor-pointer flex align-center justify-center',
        {
          'bg-white': selected
        },
        disabled && 'cursor-not-allowed text-neutral-500'
      )}
      disabled={disabled}
      type="button"
      onClick={onButtonClick}
    >
      {disabled && selected && <MdOutlineLock size={20} className="mr-1" />}
      {children}
    </button>
  );
};

const ProductTypeSelector: FC<ProductTypeSelectorProps> = ({
  disabled = false,
  isPrivate = false
}) => {
  const { t } = useTranslation(['events']);
  const { event } = useEventEditorContext();
  const { values, setFieldValue } = useFormikContext<any>();

  useIdentifyLaunchDarkly({ eventId: event._id }); // registers the event as the identity in LaunchDarkly

  // Product type descriptions
  const PRODUCT_TYPE_DESCRIPTIONS = {
    admission: t('events:pricingTiers.ticketForm.admissionDescription'),
    addon: t('events:pricingTiers.ticketForm.addonDescription'),
    bundle: t('events:pricingTiers.ticketForm.bundleDescription')
  };

  // Define product types - makes it easy to add new types in the future
  const PRODUCT_TYPES: Array<{ id: EventProductType; label: string }> = [
    { id: 'admission', label: t('events:pricingTiers.ticketForm.admission') },
    { id: 'addon', label: t('events:pricingTiers.ticketForm.addon') },
    { id: 'bundle', label: t('events:pricingTiers.ticketForm.bundle') }
  ];

  const onButtonClick = (selected: EventProductType) => {
    setFieldValue('type', selected);
  };

  const availableProductTypes = useMemo(() => {
    let types = PRODUCT_TYPES;

    if (isPrivate) {
      types = types.filter((type) => type.id !== 'bundle');
    }

    return types;
  }, [isPrivate, PRODUCT_TYPES]);

  return (
    <div className="mb-6">
      <div className="w-full p-1 md:p-[6px] bg-gray-200 rounded-lg flex items-center justify-between">
        {availableProductTypes.map((type) => (
          <TypeButton
            key={type.id}
            disabled={disabled}
            id={type.id}
            selected={values.type === type.id}
            onClick={onButtonClick}
          >
            {type.label}
          </TypeButton>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-3">
        {PRODUCT_TYPE_DESCRIPTIONS[values.type] || ''}
      </p>
    </div>
  );
};

export default ProductTypeSelector;
